import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, ListItemText, MenuItem, Modal, Select, Stack, Tab, TextField, Tooltip } from '@mui/material'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate, useLocation } from "react-router-dom";
import { toNumber, round, has } from 'lodash';
import { useDebounce } from 'use-debounce';

import style from '../../../styles/style';
import { useStateContext } from '../../../context/ContextProvider'
import axios from '../../../lib/axios';
import { createRegexFromString, deleteZero, formatHoursAndMinutes, handleAxiosError, handleInteger, handleNumber, handleZero, isValid, numericSortComparator } from '../../../helpers/helper'
import Materials from '../../../components/inventory/Items';
import AddButton from '../../../components/AddButton';
import { AUTOCOMPLETE_STYLE } from '../../../constants/constants';

const CreateProductionOrder = ({ open, handleOpen, setIsLoading, initialValues = null }) => {
    const { t } = useTranslation()
    const { company_id, choosesite, config, setCompany_id, setChoosesite, currencySymbol, features } = useStateContext()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [tabValue, setTabValue] = useState('material')
    const [count, setCount] = useState(10000)
    const [random, setRandom] = useState(Math.random())
    const [customers, setCustomers] = useState([])
    const [items, setItems] = useState([])
    const [productionSupervisors, setProductionSupervisors] = useState([])
    const [materialsList, setMaterialsList] = useState([])
    const [materials, setMaterials] = useState([])
    const [operationOptions, setOperationOptions] = useState([])
    const [rateUomOptions, setRateUomOptions] = useState([])
    const [routingOperationDetails, setRoutingOperationDetails] = useState([])
    const [manualRoutingOptions, setManualRoutingOptions] = useState([])
    const [workCenters, setWorkCenters] = useState([])
    const [operations, setOperations] = useState([])
    const [item, setItem] = useState({})
    const [totalCost, setTotalCost] = useState(0)
    const [totalQty, setTotalQty] = useState(0)
    const [hasEnabledMaterials, setHasEnabledMaterials] = useState(false)

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [productionOrderNo, setProductionOrderNo] = useState('')
    const [productionOrderDescription, setProductionOrderDescription] = useState('')
    const [itemId, setItemId] = useState('')
    const [productionOrderRoutingId, setProductionOrderRoutingId] = useState('')
    const [startDate, setStartDate] = useState(dayjs().endOf('day'))
    const [startTime, setStartTime] = useState(dayjs().endOf('minute'))
    const [dueDate, setDueDate] = useState(dayjs().endOf('day'))
    const [productionSupervisorId, setProductionSupervisorId] = useState('')
    const [comments, setComments] = useState('')
    const [qtyToProduce, setQtyToProduce] = useState(1)
    const [debouncedQtyToProduce] = useDebounce(qtyToProduce, 250)
    const prefillAppliedRef = useRef(false)

    /* KEY EVENT */
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key == 'Enter' && open) {
                createProductionOrder()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [
        open,
        customerId,
        productionOrderNo,
        productionOrderDescription,
        itemId,
        qtyToProduce,
        productionSupervisorId,
        startDate,
        startTime,
        dueDate,
        comments,
    ])


    /* UseEffect */

    useEffect(() => {
        if (!startDate.isSame(dueDate) && startDate.isAfter(dueDate)) {
            setDueDate(startDate)
        }
    }, [startDate, dueDate])

    useEffect(() => {
        if (open && isValid(company_id)) {
            getCustomers(company_id)
            getItems(company_id)
        }
    }, [company_id, open])

    useEffect(() => {
        if (open) {
            getOperationNames()
            getProductionRateUom()
        }
    }, [open])

    useEffect(() => {
        if (open && isValid(choosesite)) {
            getProductionSupervisors(choosesite)
            getManualRoutings(choosesite)
        }
    }, [choosesite, open])

    useEffect(() => {
        if (isValid(itemId)) {
            const foundItem = items.find((i) => i.id == itemId)

            if (foundItem) {
                setItem(foundItem)
                getPotentialSubAssemblies(foundItem.id)
            }
        }
    }, [itemId])

    useEffect(() => {
        if (open) {
            prefillAppliedRef.current = false
        }
    }, [open])

    /* Prefill from MRP (or any other caller) - takes priority over whatever a customer/vendor
       selection would otherwise auto-populate, since this item was explicitly requested.
       Matched by item_no (the caller's own row id rarely corresponds to the real Item id). */
    useEffect(() => {
        if (open && initialValues && items.length > 0 && !prefillAppliedRef.current) {
            prefillAppliedRef.current = true

            const matchedItem = isValid(initialValues.itemNo)
                ? items.find((i) => i.item_no === initialValues.itemNo)
                : (isValid(initialValues.itemId) ? items.find((i) => i.id == initialValues.itemId) : null)

            if (matchedItem) {
                applyItemToProduceChange(matchedItem.id)
            }
            if (isValid(initialValues.qtyToProduce)) {
                setQtyToProduce(initialValues.qtyToProduce)
            }
        }
    }, [open, initialValues, items])

    useEffect(() => {
        if (has(item, 'bill_of_material_lines')) {
            setMaterials(item.bill_of_material_lines?.map((i) => {
                return {
                    ...i,
                    item_id: i.sub_assembly_id,
                    qty: i.qty_per_assembly * qtyToProduce
                }
            }))
        }
    }, [item])

    useEffect(() => {
        const effectiveRoutingId = item?.default_routing_id || productionOrderRoutingId

        if (isValid(effectiveRoutingId)) {
            getRoutingOperations(effectiveRoutingId)
        } else {
            setRoutingOperationDetails([])
            /* no Routing to derive the site from - fall back to the Production Order's own site so the Work Center dropdown still has options */
            if (isValid(choosesite)) {
                getWorkCenters(choosesite)
            } else {
                setWorkCenters([])
            }
        }
    }, [item, productionOrderRoutingId, choosesite])

    useEffect(() => {
        setOperations(routingOperationDetails.map((op) => ({
            id: op.id,
            sequence: op.sequence,
            operation: op.operation_name,
            workCenterId: op.work_center_id ?? '',
            workCenterName: workCenters.find(wc => wc.id === op.work_center_id)?.work_center_name ?? '',
            productionRate: op.production_rate ?? '',
            rateUom: op.production_rate_uom ?? '',
            setupTime: parseSetupTime(op.setup_time)
        })))
    }, [routingOperationDetails])

    useEffect(() => {
        if (materials.length > 0) {
            const updatedMaterials = materials.map((m) => {
                return {
                    ...m,
                    qty: m.qty_per_assembly * debouncedQtyToProduce
                }
            })
            setMaterials(updatedMaterials)
        }
    }, [debouncedQtyToProduce])

    useEffect(() => {
        calculateTotalCost()
        calculateTotalQuantity()
    }, [materials])

    useEffect(() => {
        setHasEnabledMaterials(materials.length < materialsList.length)
    }, [materials, materialsList])

    /* Handlers */

    const handleClearAllMaterials = (e) => {
        e.preventDefault()

        
        if (e.detail > 0) {
            setMaterials([])
            setMaterialsList(prev => {
                const disabledListMaterials = prev.filter(m => m.disabled)
                disabledListMaterials.forEach(material => {
                    material.disabled = false
                });
                return prev
            })
            setTotalCost(0)
            setTotalQty(0)
        }
    }

    const handleClearAllOperations = (e) => {
        e.preventDefault()

        if (e.detail > 0) {
            setOperations([])
        }
    }

    const applyItemToProduceChange = (id) => {
        setItemId(id)
        setOperations([])
        setProductionOrderRoutingId('')
    }

    const handleChangeItemToProduce = (b) => {
        const newItemId = b?.id || ''

        if (materials.length === 0 && operations.length === 0) {
            applyItemToProduceChange(newItemId)
            return
        }

        Swal.fire({
            title: t('caution'),
            text: t('change_item_affects_material_and_operations'),
            icon: 'warning',
            customClass: 'warning2',
            showCloseButton: true,
            iconColor: '#FEB806',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('yes'),
            cancelButtonText: t('negatory')
        }).then((result) => {
            if (result.isConfirmed) {
                applyItemToProduceChange(newItemId)
            }
        })
    }

    const handleAddMaterial = (e) => {
        e.preventDefault()

        if (e.detail > 0) {
            let counter = 0
            let msgMaterial = ''
            let msgQty = ''
            let msgBoth = t('please_select_material_and_enter_qty')
            materials.forEach(element => {
                if (element.item_no == '') {
                    counter += 1
                    msgMaterial = t('please_select_material')
                }
                if (element.qty_per_assembly == '' || toNumber(element.qty_per_assembly) < 1) {
                    counter += 1
                    msgQty = t('please_enter_qty')
                }
            })

            if (counter > 0) {
                if (msgMaterial !== '' && msgQty === '') {
                    Swal.fire({
                        icon: "warning",
                        text: msgMaterial,
                        customClass: 'warning2',
                        showCloseButton: true,
                        iconColor: '#FFCA28'
                    })
                }
                if (msgQty !== '' && msgMaterial === '') {
                    Swal.fire({
                        icon: "warning",
                        text: msgQty,
                        customClass: 'warning2',
                        showCloseButton: true,
                        iconColor: '#FFCA28'
                    })
                }
                if (msgQty !== '' && msgMaterial !== '') {
                    Swal.fire({
                        icon: "warning",
                        text: msgBoth,
                        customClass: 'warning2',
                        showCloseButton: true,
                        iconColor: '#FFCA28'
                    })
                }


                return
            }

            const material = {
                "id": count,
                "item_id": '',
                "sub_assembly_id": '',
                "sub_assembly_no": '',
                "sub_assembly_unit_of_measure_code": '',
                "description": '',
                "unit_price": 0,
                "qty_per_assembly": 0,
                "qty": 0,
                "scrap": 0,
            }

            setMaterials((prev) => [...prev, material])
            setCount(prev => prev + 1)
        }
    }

    const handleSelectMaterial = (value, id) => {
        const materialsCopy = [...materials]
        const material = materialsCopy.find(m => m.id === id)

        material.item_id = value.id
        material.sub_assembly_id = value.id
        material.sub_assembly_no = value.item_no
        material.sub_assembly_unit_of_measure_code = value.unit_of_measure?.code ?? ''
        material.description = value.description
        material.unit_price = value.unit_price

        setMaterials(materialsCopy)
    }

    const handleMaterialQtyPerAssembly = (value, id) => {
        const materialsCopy = [...materials]
        const material = materialsCopy.find(m => m.id === id)

        material.qty_per_assembly = value
        material.qty = value * qtyToProduce

        setMaterials(materialsCopy)

        calculateTotalQuantity()
    }

    const handleMaterialOperation = (value, id) => {
        const materialsCopy = [...materials]
        const material = materialsCopy.find(m => m.id === id)

        material.routing_operation_id = value

        setMaterials(materialsCopy)
    }

    const handleMaterialScrap = (value, id) => {
        const materialsCopy = [...materials]
        const material = materialsCopy.find(m => m.id === id)

        material.scrap = value

        setMaterials(materialsCopy)
    }

    const handleMaterialUnitPrice = (value, id) => {
        const materialsCopy = [...materials]
        const material = materialsCopy.find(m => m.id === id)

        material.unit_price = value

        setMaterials(materialsCopy)
    }

    const calculateTotalCost = () => {
        let tcost = 0
        materials.forEach(material => {
            tcost += (toNumber(material.qty_per_assembly) * toNumber(material.unit_price)) * toNumber(qtyToProduce)
        })
        setTotalCost(round(tcost, 2))
    }

    const calculateTotalQuantity = () => {
        let tqty = 0
        materials.forEach(material => {
            tqty += toNumber(material.qty_per_assembly) * toNumber(qtyToProduce)
        })
        setTotalQty(tqty)
    }

    const isOperationComplete = (op) => (
        isValid(op.operation)
        && isValid(op.workCenterId)
        && op.productionRate !== '' && toNumber(op.productionRate) > 0
        && isValid(op.rateUom)
        && op.setupTime !== ''
    )

    const getMissingOperationFields = (op) => {
        const missing = []
        if (!isValid(op.operation)) missing.push({ label: t('operation'), verb: 'select' })
        if (!isValid(op.workCenterId)) missing.push({ label: t('work_center'), verb: 'select' })
        if (!(op.productionRate !== '' && toNumber(op.productionRate) > 0)) missing.push({ label: t('production_rate'), verb: 'enter' })
        if (!isValid(op.rateUom)) missing.push({ label: t('rate_uom'), verb: 'select' })
        if (op.setupTime === '') missing.push({ label: t('setup_minutes'), verb: 'enter' })
        return missing
    }

    const showIncompleteOperationWarning = () => {
        const incompleteOperation = operations.find((op) => !isOperationComplete(op))

        if (!incompleteOperation) return false

        const missing = getMissingOperationFields(incompleteOperation)
        const text = missing.length === 1
            ? (missing[0].verb === 'select' ? t('please_select_the_x', { x: missing[0].label }) : t('please_enter_the_x', { x: missing[0].label }))
            : t('please_complete_the_following_fields', { fields: missing.map(m => m.label).join(', ') })

        Swal.fire({
            icon: "warning",
            text,
            customClass: 'warning2',
            showCloseButton: true,
            iconColor: '#FFCA28'
        })
        return true
    }

    const handleAddOperation = (e) => {
        e.preventDefault()

        if (e.detail > 0) {
            if (showIncompleteOperationWarning()) {
                return
            }

            const nextSequence = operations.length > 0 ? Math.max(...operations.map(row => row.sequence)) + 10 : 10
            setOperations((prev) => [...prev, {
                id: Date.now(),
                sequence: nextSequence,
                operation: '',
                workCenterId: '',
                workCenterName: '',
                productionRate: '',
                rateUom: '',
                setupTime: ''
            }])
        }
    }

    const deleteOperation = (id) => {
        setOperations((prev) => prev.filter((row) => row.id !== id))
    }

    const parseSetupTime = (value) => {
        if (!isValid(value)) return ''
        if (typeof value === 'string' && value.includes(':')) {
            const [h, m, s] = value.split(':').map(Number)
            return Number((h + (m / 60) + ((s || 0) / 3600)).toFixed(2))
        }
        return Number(value)
    }

    const handleOperationChange = (value, id) => {
        const operationsCopy = [...operations]
        const operation = operationsCopy.find(o => o.id === id)

        operation.operation = value

        const matchedRoutingOperation = routingOperationDetails.find(op => op.operation_name === value)
        if (matchedRoutingOperation) {
            const matchedWorkCenter = workCenters.find(wc => wc.id === matchedRoutingOperation.work_center_id)

            operation.workCenterId = matchedRoutingOperation.work_center_id ?? ''
            operation.workCenterName = matchedWorkCenter?.work_center_name ?? ''
            operation.productionRate = matchedRoutingOperation.production_rate ?? ''
            operation.rateUom = matchedRoutingOperation.production_rate_uom ?? ''
            operation.setupTime = parseSetupTime(matchedRoutingOperation.setup_time)
        }

        setOperations(operationsCopy)
    }

    const handleWorkCenterChange = (value, id) => {
        const operationsCopy = [...operations]
        const operation = operationsCopy.find(o => o.id === id)

        operation.workCenterId = value?.id || ''
        operation.workCenterName = value?.label || ''

        setOperations(operationsCopy)
    }

    const handleProductionRateChange = (value, id) => {
        const operationsCopy = [...operations]
        const operation = operationsCopy.find(o => o.id === id)

        operation.productionRate = value

        setOperations(operationsCopy)
    }

    const handleRateUomChange = (value, id) => {
        const operationsCopy = [...operations]
        const operation = operationsCopy.find(o => o.id === id)

        operation.rateUom = value

        setOperations(operationsCopy)
    }

    const handleSetupTimeChange = (value, id) => {
        const operationsCopy = [...operations]
        const operation = operationsCopy.find(o => o.id === id)

        operation.setupTime = value

        setOperations(operationsCopy)
    }

    const handleToggleModal = () => {
        handleOpen()
        setCustomerId('')
        setProductionOrderNo('')
        setProductionOrderDescription('')
        setItemId('')
        setItem({})
        setProductionOrderRoutingId('')
        setQtyToProduce(0)
        setProductionSupervisorId('')
        setStartDate(dayjs().endOf('day'))
        setStartTime(dayjs().endOf('minute'))
        setDueDate(dayjs().endOf('day'))
        setComments('')
        setMaterials([])
        setOperations([])
    }

    /* API Requests */
    const getCustomers = async (companyId) => {
        setIsLoading(true)
        setCustomerId('')

        await axios
            .get(`/api/customers?company_id=${companyId}&blocked=0`, config)
            .then(res => {
                const customers = res.data
                setCustomers(customers)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getItems = async (companyId) => {
        setIsLoading(true)
        setItemId('')
        setItem({})

        await axios
            .get(`/api/items?company_id=${companyId}&with=billOfMaterialLines`, config)
            .then(res => {
                const data = res.data.filter((item) => item.item_type_key != 'other')
                setItems(data)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getPotentialSubAssemblies = async (itemId) => {
        setIsLoading(true)

        await axios.get(`/api/items/${itemId}/potential-sub-assemblies`, config)
            .then(res => {
                const data = res.data
                setMaterialsList(data.map((d) => ({ ...d, disabled: false })))
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getOperationNames = async () => {
        await axios
            .get('/api/routing-operation-names', config)
            .then(res => {
                setOperationOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getProductionRateUom = async () => {
        await axios
            .get('/api/production-rate-uom', config)
            .then(res => {
                setRateUomOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getRoutingOperations = async (routingId) => {
        await axios.get(`/api/routings/${routingId}`, config)
            .then(res => {
                const routing = res.data?.data
                const routingOperations = routing?.routing_operations ?? []
                setRoutingOperationDetails(routingOperations)

                if (isValid(routing?.site_id)) {
                    getWorkCenters(routing.site_id)
                } else {
                    setWorkCenters([])
                }
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getWorkCenters = async (site) => {
        await axios
            .get(`/api/work-centers?site_id=${site}`, config)
            .then(res => {
                const activeWorkCenters = (res.data?.data ?? []).filter(wc => String(wc.status).toLowerCase() === 'active')
                setWorkCenters(activeWorkCenters)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getProductionSupervisors = async (siteId) => {
        setIsLoading(true)
        setProductionSupervisorId('')

        await axios
            .get(`/api/users?site_id=${siteId}&role=production_supervisor`, config)
            .then(res => {
                const data = res.data?.data
                setProductionSupervisors(data)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getManualRoutings = async (site) => {
        await axios
            .get(`/api/routings?site_id=${site}&status=ACTIVE`, config)
            .then(res => {
                const activeRoutings = (res.data?.data ?? []).filter(r => String(r.status).toLowerCase() === 'active')
                setManualRoutingOptions(activeRoutings.map(r => ({ id: r.id, label: `${r.routing_code} - ${r.routing_name}` })))
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const createProductionOrder = async ({ shouldProceedAnyway = false } = {}) => {
        if (showIncompleteOperationWarning()) {
            return
        }

        setIsLoading(true)
        const formData = {}

        formData['no'] = productionOrderNo
        formData['description'] = productionOrderDescription
        formData['qty_to_produce'] = qtyToProduce
        formData['start_date'] = dayjs(startDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        formData['start_time'] = dayjs(startTime).endOf('minute').format('YYYY-MM-DD HH:mm:ss')
        formData['due_date'] = dayjs(dueDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        formData['site_id'] = choosesite
        formData['company_id'] = company_id
        formData['item_id'] = itemId
        formData['production_order_routing_id'] = productionOrderRoutingId || null
        formData['customer_id'] = customerId
        formData['production_supervisor_id'] = productionSupervisorId
        formData['comments'] = comments
        formData['production_order_items'] = materials
        formData['routing_operations'] = operations.map((op) => ({
            sequence: op.sequence,
            operation_name: op.operation,
            work_center_id: op.workCenterId,
            production_rate: op.productionRate,
            production_rate_uom: op.rateUom,
            setup_time: op.setupTime,
            planned_time: getPlannedTime(op),
            estimated_cost: getEstimatedCost(op)
        }))
        formData['proceed_anyway'] = shouldProceedAnyway

        await axios
            .post(`/api/production-orders`, formData, config)
            .then((data) => {
                if (data.status === 202) {
                    Swal.fire({
                        title: t('caution'),
                        text: data.data.accepted?.description ?? '',
                        icon: 'warning',
                        customClass: 'warning2',
                        showCloseButton: true,
                        iconColor: '#FEB806',
                        reverseButtons: true,
                        showCancelButton: true,

                        confirmButtonText: t('yes'),
                        cancelButtonText: t('negatory')
                    }).then((result) => {
                        if (result.isConfirmed) {
                            createProductionOrder({
                                shouldProceedAnyway: true
                            })
                        }
                    })
                } else {
                    Swal.fire({
                        icon: "success",
                        text: data.data.success.message,
                        customClass: 'success',
                        showCloseButton: true,
                        iconColor: '#00B78E'
                    })

                    navigate("/production-order-board")

                    handleToggleModal()
                }
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const materialOperationOptions = operations.map((op) => ({
        value: op.id,
        label: `${op.sequence} - ${operationOptions.find(o => o.value === op.operation)?.label ?? op.operation} (${workCenters.find(wc => wc.id === op.workCenterId)?.work_center_name ?? op.workCenterName ?? ''})`
    }))

    const materialsColumns = [
        {
            field: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? 'description' : 'sub_assembly_no',
            headerName: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? (t('description') + ' *') : (t('item_number_abbr') + ' *'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                if (features?.ITEM_NO_DESCRIPTION_SWITCH == 1) {
                    return <MaterialSelection params={params} t={t} materialsList={materialsList} setMaterialsList={setMaterialsList} handleSelectMaterial={handleSelectMaterial} type="description" />
                } else {
                    return <MaterialSelection params={params} t={t} materialsList={materialsList} setMaterialsList={setMaterialsList} handleSelectMaterial={handleSelectMaterial} type="no" />
                }
            }
        },
        {
            field: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? 'sub_assembly_no' : 'description',
            headerName: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? t('item_number_abbr') : t('description'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                if (features?.ITEM_NO_DESCRIPTION_SWITCH == 1) {
                    return <SimpleMaterialNo params={params} t={t} />
                } else {
                    return <SimpleMaterialDescription params={params} t={t} />
                }
            }
        },
        {
            field: 'routing_operation_id',
            headerName: t('operation'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialOperation params={params} t={t} operationOptions={materialOperationOptions} handleMaterialOperation={handleMaterialOperation} />
        },
        {
            field: 'qty_per_assembly',
            headerName: t('qty_per_assembly') + ' *',
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialQtyPerAssembly params={params} t={t} materialsList={materialsList} handleMaterialQtyPerAssembly={handleMaterialQtyPerAssembly} />
        },
        {
            field: 'qty',
            headerName: t('totalQty'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialQty params={params} t={t} qtyToProduce={qtyToProduce} />,
            valueGetter: (params) => params.row.qty_per_assembly * qtyToProduce,
            sortComparator: numericSortComparator
        },
        {
            field: 'sub_assembly_unit_of_measure_code',
            headerName: t('unit_of_measure'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialUnitOfMeasure params={params} t={t} />
        },
        {
            field: 'scrap',
            headerName: `${t('scrap')} (%)`,
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialScrap params={params} t={t} materialsList={materialsList} handleMaterialScrap={handleMaterialScrap} />
        },
        {
            field: 'unit_price',
            headerName: t('unit_price'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialUnitPrice params={params} t={t} materialsList={materialsList} currencySymbol={currencySymbol} handleMaterialUnitPrice={handleMaterialUnitPrice} />
        },
        {
            field: 'total_price',
            headerName: t('total_price'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => <MaterialTotalPrice params={params} t={t} currencySymbol={currencySymbol} calculateTotalCost={calculateTotalCost} qtyToProduce={qtyToProduce} />,
            valueGetter: (params) => (params.row.qty_per_assembly * params.row.unit_price) * qtyToProduce,
            sortComparator: numericSortComparator
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            cellClassName: 'padding-0',
            renderCell: (params) => <MaterialActions params={params} materials={materials} setMaterials={setMaterials} setMaterialsList={setMaterialsList} />
        }
    ];

    const getRunQuantity = (op) => {
        if (op.rateUom === 'PIECES_PER_HOUR') {
            return materials.filter(m => m.routing_operation_id === op.id).reduce((sum, m) => sum + toNumber(m.qty), 0)
        }
        return toNumber(qtyToProduce)
    }

    const getRunTime = (op) => {
        const rate = toNumber(op.productionRate)
        return rate > 0 ? round(getRunQuantity(op) / rate, 2) : 0
    }

    const getPlannedTime = (op) => {
        const rate = toNumber(op.productionRate)
        return rate > 0 ? round((getRunQuantity(op) / rate) + (toNumber(op.setupTime) / 60), 2) : 0
    }

    const getEstimatedCost = (op) => {
        const costPerHour = toNumber(workCenters.find(wc => wc.id === op.workCenterId)?.cost_per_hour)
        const cost = getPlannedTime(op) * costPerHour
        return isNaN(cost) ? 0 : round(cost, 2)
    }

    const getAvailableHoursPerDay = (op) => toNumber(workCenters.find(wc => wc.id === op.workCenterId)?.available_hours)

    const getEstimatedProductionDays = (op) => {
        const hoursPerDay = getAvailableHoursPerDay(op)
        return hoursPerDay > 0 ? round(getPlannedTime(op) / hoursPerDay, 2) : 0
    }

    const totalOperationCost = operations.reduce((sum, op) => sum + getEstimatedCost(op), 0)

    const operationsColumns = [
        { field: 'sequence', headerName: t('seq'), flex: 0.5, minWidth: 90 },
        {
            field: 'operation',
            headerName: t('operation') + ' *',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => <OperationSelect params={params} t={t} operationOptions={operationOptions} handleOperationChange={handleOperationChange} />
        },
        {
            field: 'workCenterName',
            headerName: t('work_center') + ' *',
            flex: 1,
            minWidth: 170,
            renderCell: (params) => <OperationWorkCenter params={params} workCenters={workCenters} handleWorkCenterChange={handleWorkCenterChange} />
        },
        {
            field: 'productionRate',
            headerName: t('production_rate') + ' *',
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => <OperationRate params={params} handleProductionRateChange={handleProductionRateChange} />
        },
        {
            field: 'rateUom',
            headerName: t('rate_uom') + ' *',
            flex: 0.9,
            minWidth: 150,
            renderCell: (params) => <OperationRateUom params={params} t={t} rateUomOptions={rateUomOptions} handleRateUomChange={handleRateUomChange} />
        },
        {
            field: 'runTime',
            headerName: t('run_time'),
            flex: 0.7,
            minWidth: 110,
            valueGetter: (params) => getRunTime(params.row),
            valueFormatter: ({ value }) => `${value} h`
        },
        {
            field: 'setupTime',
            headerName: t('setup_minutes') + ' *',
            flex: 0.7,
            minWidth: 110,
            renderCell: (params) => <OperationSetupTime params={params} handleSetupTimeChange={handleSetupTimeChange} />
        },
        {
            field: 'plannedTime',
            headerName: t('planned_time'),
            flex: 0.7,
            minWidth: 160,
            valueGetter: (params) => getPlannedTime(params.row),
            valueFormatter: ({ value }) => formatHoursAndMinutes(value)
        },
        {
            field: 'availableHoursPerDay',
            headerName: t('available_hours'),
            flex: 0.7,
            minWidth: 130,
            valueGetter: (params) => getAvailableHoursPerDay(params.row),
            valueFormatter: ({ value }) => `${value} h`
        },
        {
            field: 'estimatedProductionDays',
            headerName: t('estimated_production_days'),
            flex: 0.7,
            minWidth: 150,
            valueGetter: (params) => getEstimatedProductionDays(params.row),
            valueFormatter: ({ value }) => `${value} ${t('days')}`
        },
        {
            field: 'estimatedCost',
            headerName: t('estimated_cost'),
            flex: 0.7,
            minWidth: 120,
            valueGetter: (params) => getEstimatedCost(params.row),
            valueFormatter: ({ value }) => `${currencySymbol}${toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.5,
            minWidth: 90,
            cellClassName: 'padding-0',
            renderCell: (params) => (
                <div className='flex justify-between'>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteOperation(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <>
            <Modal open={open} onClose={handleToggleModal}>
                <Box sx={style}>
                    <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-center justify-between w-full leading-none'>
                            <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {t('create_new_production_order')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                                <button onClick={handleToggleModal}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>
                    <div className='p-5 pb-12 flex justify-between gap-3 flex-col'>
                        <div
                            className='bg-white p-6 pb-[12px] pt-0 rounded-md w-full h-fit'
                            style={{
                                boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                            }}
                        >
                            <div className='pb-6 pt-1 flex gap-3 items-center'>
                                <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                            </div>

                            <div className='flex flex-col md:flex-row gap-0 md:gap-4'>
                                <div className='flex flex-col w-full md:w-1/2'>
                                    {/* Customer */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('customer')}</p>
                                        <div className='w-full'>
                                            <Autocomplete
                                                disablePortal
                                                disableClearable
                                                id="customer-autocomplete"
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '0 !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                options={customers?.map((option) => ({ id: option.id, label: option.name }))}
                                                value={customerId ? { id: customerId, label: customers?.find((c) => c.id === customerId)?.name || '' } : null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => {
                                                    return (
                                                        <li {...props} key={`customer-${option.id}`}>
                                                            {option.label}
                                                        </li>
                                                    )
                                                }}
                                                onChange={(a, b) => setCustomerId(b?.id || '')}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </div>
                                    </div>

                                    {/* Production Order No. */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('production_order_no')} *</p>
                                        <div className='w-full'>
                                            <TextField
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '9px !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                type="text"
                                                name="production_order_no"
                                                value={productionOrderNo}
                                                onChange={(e) => { setProductionOrderNo(e.target.value) }}
                                            />
                                        </div>
                                    </div>

                                    {/* Production Order Description */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('production_order_description')} *</p>
                                        <div className='w-full'>
                                            <TextField
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '9px !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                type="text"
                                                name="production_order_description"
                                                value={productionOrderDescription}
                                                onChange={(e) => { setProductionOrderDescription(e.target.value) }}
                                            />
                                        </div>
                                    </div>

                                    {/* Item To Produce */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('item_to_produce')} *</p>
                                        <Autocomplete
                                            disablePortal
                                            disableClearable
                                            id="item-autocomplete"
                                            sx={{
                                                width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                            }}
                                            value={itemId
                                                ? {
                                                    id: itemId,
                                                    label: (features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? items?.find((i) => i.id === itemId)?.description : items?.find((i) => i.id === itemId)?.item_no) ?? ''
                                                } : null
                                            }
                                            options={items?.map((option) => ({
                                                id: option.id,
                                                label: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? option.description : option.item_no,
                                                description: features?.ITEM_NO_DESCRIPTION_SWITCH == 1 ? option.item_no : option?.description,
                                                disabled: false
                                            }))}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            renderOption={(props, option) => {
                                                return (
                                                    <li {...props} key={`item-option-${option.id}`}>
                                                        <ListItemText
                                                            primary={option.label}
                                                            secondary={option.description}
                                                        />
                                                    </li>
                                                )
                                            }}
                                            renderInput={(params) => <TextField {...params} />}
                                            onChange={(a, b) => handleChangeItemToProduce(b)}
                                        />
                                    </div>

                                    {isValid(itemId) && !isValid(item?.default_routing_id) ? (
                                        <div className='w-full pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('routing')}</p>
                                            <Autocomplete
                                                disablePortal
                                                id="production-order-routing-autocomplete"
                                                sx={{
                                                    width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                                    boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                                }}
                                                options={manualRoutingOptions}
                                                value={productionOrderRoutingId ? manualRoutingOptions.find((o) => o.id === productionOrderRoutingId) ?? null : null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                onChange={(a, b) => setProductionOrderRoutingId(b?.id || '')}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </div>
                                    ) : null}

                                    {/* Quantity To Produce */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('quantity_to_produce')} *</p>
                                        <Stack direction='row'>
                                            <div className='w-2/3'>
                                                <TextField
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-root': {
                                                            borderTopRightRadius: '0px',
                                                            borderBottomRightRadius: '0px',
                                                        },
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    type="text"
                                                    name="quantity_to_produce"
                                                    value={qtyToProduce}
                                                    onChange={(e) => handleInteger({
                                                        event: e,
                                                        setter: setQtyToProduce
                                                    })}
                                                    onBlur={e => handleZero({
                                                        event: e,
                                                        setter: setQtyToProduce
                                                    })}
                                                    onFocus={e => deleteZero({
                                                        event: e,
                                                        setter: setQtyToProduce,
                                                        value: qtyToProduce
                                                    })}
                                                />
                                            </div>
                                            <div className='w-1/3'>
                                                <TextField
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-root': {
                                                            borderTopLeftRadius: '0px',
                                                            borderBottomLeftRadius: '0px'
                                                        },
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    type="text"
                                                    name="unit_of_measure"
                                                    value={item?.unit_of_measure_name ?? ''}
                                                    disabled
                                                />
                                            </div>
                                        </Stack>
                                    </div>
                                </div>

                                <div className='flex flex-col w-full md:w-1/2'>
                                    <div className='lg:flex gap-3 w-full'>
                                        {/* Start Date */}
                                        <div className='w-full lg:w-1/2 pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('start_date')}</p>
                                            <div className='w-full'>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <MobileDatePicker
                                                        maxDate={dueDate}
                                                        sx={{
                                                            width: '100%',
                                                            background: '#FCFCFC',
                                                            '.MuiOutlinedInput-input': {
                                                                padding: '9px !important'
                                                            },
                                                            boxShadow:
                                                                'none',
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                border: '1px solid #EEEFF2 !important'
                                                            }
                                                        }}
                                                        format="YYYY-MM-DD"
                                                        value={startDate}
                                                        onChange={(newValue) => setStartDate(newValue)}
                                                    />
                                                </LocalizationProvider>
                                            </div>
                                        </div>

                                        {/* Start Time */}
                                        <div className='w-full lg:w-1/2 pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('start_time')}</p>
                                            <div className='w-full'>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DemoContainer sx={{ padding: '0' }} components={['TimePicker', 'TimePicker']}>
                                                        <MobileTimePicker
                                                            sx={{
                                                                width: '100%',
                                                                background: '#FCFCFC',
                                                                '.MuiOutlinedInput-input': {
                                                                    padding: '9px !important'
                                                                },
                                                                boxShadow: 'none',
                                                                '.MuiOutlinedInput-notchedOutline': {
                                                                    border: '1px solid #EEEFF2 !important'
                                                                }
                                                            }}
                                                            value={startTime}
                                                            onChange={(newValue) => setStartTime(newValue)}
                                                        />
                                                    </DemoContainer>
                                                </LocalizationProvider>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Due Date */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('due_date')}</p>
                                        <div className='w-full'>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <MobileDatePicker
                                                    minDate={startDate}
                                                    sx={{
                                                        width: '100%',
                                                        background: '#FCFCFC',
                                                        '.MuiOutlinedInput-input': {
                                                            padding: '9px !important'
                                                        },
                                                        boxShadow: 'none',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            border: '1px solid #EEEFF2 !important'
                                                        }
                                                    }}
                                                    format="YYYY-MM-DD"
                                                    value={dueDate}
                                                    onChange={(newValue) => setDueDate(newValue)}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    </div>

                                    {/* Production Supervisor */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('production_supervisor')} *</p>
                                        <div className='w-full'>
                                            <Autocomplete
                                                disablePortal
                                                disableClearable
                                                id="production-supervisor-autocomplete"
                                                sx={{
                                                    width: '100%',
                                                    background: '#FCFCFC',
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '0 !important'
                                                    },
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: '1px solid #EEEFF2 !important'
                                                    }
                                                }}
                                                options={productionSupervisors?.map((option) => ({ id: option.id, label: option.name }))}
                                                value={productionSupervisorId ? { id: productionSupervisorId, label: productionSupervisors?.find((ps) => ps.id === productionSupervisorId)?.name || '' } : null}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => {
                                                    return (
                                                        <li {...props} key={`production-supervisor-${option.id}`}>
                                                            {option.label}
                                                        </li>
                                                    )
                                                }}
                                                onChange={(a, b) => setProductionSupervisorId(b?.id || '')}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </div>
                                    </div>

                                    <div className='w-full'>
                                        <p className="text-[14px] text-[#718096]">{t('comments_and_special_instructions')}</p>
                                        <TextField
                                            multiline
                                            rows={3}
                                            value={comments}
                                            onChange={e => setComments(e.target.value)}
                                            sx={{
                                                width: '100%',
                                                background: '#FCFCFC',
                                                '.MuiOutlinedInput-input': {
                                                    padding: '9px !important'
                                                },
                                                boxShadow: 'none',
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    border: '1px solid #EEEFF2 !important'
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className='bg-white p-6 pb-[12px] pt-0 mb-3 rounded-md w-full h-fit'
                            style={{
                                boxShadow: `0px 1px 2px 0px #1018280F, 0px 1px 2px 0px #1018280F, 0px -1px 4px 0px #1018280F`
                            }}
                        >
                            <TabContext value={tabValue}>
                                <TabList onChange={(e, value) => setTabValue(value)} aria-label="production order tabs">
                                    <Tab sx={{ textTransform: 'none' }} value="material" label={t('material_list')} icon={<i className="fa-solid fa-boxes-stacked"></i>} iconPosition='start' />
                                    <Tab sx={{ textTransform: 'none' }} value="operations" label={t('operations')} icon={<i className="fa-solid fa-gears"></i>} iconPosition='start' />
                                </TabList>

                                <TabPanel value="material" sx={{ padding: 0 }}>
                                    <div className='flex justify-end p-5 pt-0 border-b'>
                                        <Tooltip disableInteractive title={!isValid(itemId) ? t('select_item_first') : (!hasEnabledMaterials ? t('no_sub_assemblies_found') : t('add_new_line'))} placement='left'>
                                            <div>
                                                <AddButton disabled={!isValid(itemId) || !hasEnabledMaterials} onClick={handleAddMaterial}><i className="fa-solid fa-plus"></i></AddButton>
                                            </div>
                                        </Tooltip>
                                    </div>

                                    {/* MATERIAL LINES */}
                                    <div className='border-b border-l border-r rounded'>
                                        {
                                            materials.length > 0 ?
                                                <div className='flex justify-end w-full p-2'>
                                                    <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={handleClearAllMaterials}>{t('clear_all')}</button>
                                                </div>
                                                :
                                                null
                                        }
                                        <Materials items={materials} columns={materialsColumns} random={random} />
                                    </div>

                                    <div className='flex justify-end w-full p-5'>
                                        <div className='flex flex-col gap-y-4 w-full sm:w-1/3'>
                                            <div className='flex justify-between items-center'>
                                                <p className="font-[400] text-[#88909C] text-[14px]">{t('total_cost')}</p>
                                                <p className='font-[500] text-[16px]'>{currencySymbol}{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                            </div>
                                            <div className='flex justify-between items-center'>
                                                <p className="font-[400] text-[#88909C] text-[14px]">{t('totalQty')}</p>
                                                <p className='font-[500] text-[16px]'>{totalQty.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel value="operations" sx={{ padding: 0 }}>
                                    <div className='flex justify-end p-5 pt-0 border-b'>
                                        <Tooltip disableInteractive title={!isValid(itemId) ? t('select_item_first') : t('add_new_line')} placement='left'>
                                            <div>
                                                <AddButton disabled={!isValid(itemId)} onClick={handleAddOperation}><i className="fa-solid fa-plus"></i></AddButton>
                                            </div>
                                        </Tooltip>
                                    </div>

                                    <div className='border-b border-l border-r rounded'>
                                        {
                                            operations.length > 0 ?
                                                <div className='flex justify-end w-full p-2'>
                                                    <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={handleClearAllOperations}>{t('clear_all')}</button>
                                                </div>
                                                :
                                                null
                                        }
                                        <Materials items={operations} columns={operationsColumns} random={random} />
                                    </div>

                                    <div className='flex justify-end w-full p-5'>
                                        <div className='flex flex-col gap-y-4 w-full sm:w-1/3'>
                                            <div className='flex justify-between items-center'>
                                                <p className="font-[400] text-[#88909C] text-[14px]">{t('total_operation_cost')}</p>
                                                <p className='font-[500] text-[16px]'>{currencySymbol}{totalOperationCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                            </TabContext>
                        </div>

                        <div className='w-full h-fit'>
                            <div className='w-full flex justify-end'>
                                <button type="button" onClick={createProductionOrder} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                                    <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                                </button>
                            </div>
                        </div>
                    </div>
                </Box>
            </Modal>
        </>
    )
}

export default CreateProductionOrder

const MaterialSelection = ({ params, t, materialsList, setMaterialsList, handleSelectMaterial, type }) => {
    const [keys] = useState(() => {
        const defaultKeys = {
            menuItem: 'item_no',
            textField: 'sub_assembly_no'
        };

        if (type == 'description') {
            return {
                menuItem: 'description',
                textField: 'description'
            }
        } else {
            return defaultKeys;
        }
    })
    const [material, setMaterial] = useState(' ')
    const [exists, setExists] = useState(true)

    const handleChange = (e) => {
        setMaterial(prev => {
            if (prev == ' ') {
                return prev
            }
            if (prev) {
                prev.disabled = false
            }
            return prev
        })
        setMaterial(e.target.value)
        handleSelectMaterial(e.target.value, params.row.id)
        setMaterialsList(prev => {
            const target = prev.find(m => m.id == e.target.value.id)
            target.disabled = true
            return prev
        })
    }

    useEffect(() => {
        if (materialsList.length >= 0) {
            if (params.row.sub_assembly_id !== '') {
                const target = materialsList?.find((m) => m.id === params.row.sub_assembly_id)

                if (target) {
                    target.disabled = true
                    setMaterial(target)

                    setExists(true)
                } else {
                    setMaterial(' ')
                    setExists(false)
                }
            } else {
                setExists(true)
            }
        }
    }, [materialsList])

    return (
        <>
            {exists ? (
                type === 'description' ? (
                    <Autocomplete
                        disableClearable
                        className='w-full'
                        options={materialsList ?? []}
                        getOptionLabel={(m) => m[keys.menuItem] ?? ''}
                        getOptionDisabled={(m) => !!m.disabled}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={material === ' ' ? null : material}
                        onChange={(e, newValue) => newValue && handleChange({ target: { value: newValue } })}
                        renderInput={(p) => <TextField {...p} variant='standard' placeholder={t('select_item')} />}
                    />
                ) : (
                    <FormControl variant="standard" sx={{ width: '100%' }}>
                        <Select
                            defaultValue=' '
                            value={material}
                            onChange={e => handleChange(e)}
                        >
                            <MenuItem
                                value=' '
                                disabled
                                sx={{ fontStyle: 'italic', display: 'none' }}
                            >
                                {t('select_item')}
                            </MenuItem>
                            {
                                materialsList?.map((m, index) => (
                                    <MenuItem
                                        value={m}
                                        key={`materialslist-${m.id}`}
                                        disabled={m.disabled}
                                    >
                                        {m[keys.menuItem]}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                )
            ) : (
                <TextField
                    type="text"
                    variant='standard'
                    disabled
                    className='w-full mb-5 px-0 pt-0'
                    value={params.row[keys.textField]}
                />
            )}
        </>
    )
}

const SimpleMaterialNo = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
            disabled
        />
    )
}

const SimpleMaterialDescription = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
            disabled
        />
    )
}

const MaterialQtyPerAssembly = ({ params, t, materialsList, handleMaterialQtyPerAssembly }) => {
    const [qty, setQty] = useState(params.value)
    const [debouncedQty] = useDebounce(qty, 250)
    const [disabled, setDisabled] = useState(false)

    const handleQty = (e) => {
        const regex = /^(-?\d{0,5})$/i
        if (regex.test(e.target.value)) {
            if (e.target.value < 0 || e.target.value === '-') {
                setQty('')
            } else {
                setQty(e.target.value)
            }
        }
    }

    useEffect(() => {
        if (debouncedQty && isFinite(debouncedQty)) {
            handleMaterialQtyPerAssembly(toNumber(debouncedQty), params.row.id)
        } else {
            handleMaterialQtyPerAssembly(0, params.row.id)
        }
    }, [debouncedQty])

    useEffect(() => {
        if (materialsList.length >= 0) {
            if (params.row.item_id !== '') {
                const target = materialsList?.find((m) => m.id === params.row.item_id)

                if (target) {
                    setDisabled(false)
                } else {
                    setDisabled(true)
                }
            } else {
                setDisabled(false)
            }
        }
    }, [materialsList])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={qty}
            onChange={(e) => { handleQty(e) }}
            onBlur={e => handleZero({
                event: e,
                setter: setQty
            })}
            onFocus={e => deleteZero({
                event: e,
                setter: setQty,
                value: qty
            })}
        />
    )
}

const MaterialQty = ({ params, t, qtyToProduce }) => {
    const [qty, setQty] = useState(0)

    useEffect(() => {
        setQty(params.row.qty_per_assembly * qtyToProduce)
    }, [params.row.qty_per_assembly, qtyToProduce])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={(toNumber(qty) || 0).toLocaleString()}
            disabled
        />
    )
}

const MaterialOperation = ({ params, t, operationOptions, handleMaterialOperation }) => {
    return (
        <FormControl variant="standard" sx={{ width: '100%' }}>
            <Select
                disabled={params.row.item_id === ''}
                displayEmpty
                value={params.row.routing_operation_id ?? ''}
                onChange={(e) => handleMaterialOperation(e.target.value, params.row.id)}
            >
                <MenuItem value="">
                    <em>{t('select_operation')}</em>
                </MenuItem>
                {operationOptions.map((option) => (
                    <MenuItem key={`material-operation-${params.row.id}-${option.value}`} value={option.value}>{option.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const MaterialUnitOfMeasure = ({ params, t }) => {
    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={params.value}
            disabled
        />
    )
}

const MaterialScrap = ({ params, t, materialsList, handleMaterialScrap }) => {
    const [scrap, setScrap] = useState(params.value)
    const [debouncedScrap] = useDebounce(scrap, 250)
    const [disabled, setDisabled] = useState(false)

    const handleScrap = (e) => {
        const regex = /^(-?\d*)((\.(\d{0,2})?)?)$/i
        if (regex.test(e.target.value)) {
            if (e.target.value < 0 || e.target.value === '-') {
                setScrap('')
            } else if (e.target.value > 100) {
                Swal.fire({
                    icon: "warning",
                    text: t('scrap_cannot_exceed_x_pct', { x: 100 }),
                    customClass: 'warning2',
                    showCloseButton: true,
                    iconColor: '#FFCA28'
                })
                setScrap(100)
            } else {
                setScrap(e.target.value)
            }
        }
    }

    useEffect(() => {
        if (debouncedScrap && isFinite(debouncedScrap)) {
            handleMaterialScrap(toNumber(debouncedScrap), params.row.id)
        } else {
            handleMaterialScrap(0, params.row.id)
        }
    }, [debouncedScrap])

    useEffect(() => {
        if (materialsList.length >= 0) {
            if (params.row.item_id !== '') {
                const target = materialsList?.find((m) => m.id === params.row.item_id)

                if (target) {
                    setDisabled(false)
                } else {
                    setDisabled(true)
                }
            } else {
                setDisabled(false)
            }
        }
    }, [materialsList])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={scrap}
            onChange={(e) => { handleScrap(e) }}
            onBlur={e => handleZero({
                event: e,
                setter: setScrap
            })}
            onFocus={e => deleteZero({
                event: e,
                setter: setScrap,
                value: scrap
            })}
        />
    )
}

const MaterialUnitPrice = ({ params, t, materialsList, currencySymbol, handleMaterialUnitPrice }) => {
    const [unitPrice, setUnitPrice] = useState(params.value)
    const [debouncedUnitPrice] = useDebounce(unitPrice, 250)
    const [disabled, setDisabled] = useState(false)

    useEffect(() => {
        setUnitPrice(params.value)
    }, [params.value])

    useEffect(() => {
        if (debouncedUnitPrice && isFinite(debouncedUnitPrice)) {
            handleMaterialUnitPrice(toNumber(debouncedUnitPrice), params.row.id)
        } else {
            handleMaterialUnitPrice(0, params.row.id)
        }
    }, [debouncedUnitPrice])

    useEffect(() => {
        if (materialsList.length >= 0) {
            if (params.row.item_id !== '') {
                const target = materialsList?.find((m) => m.id === params.row.item_id)

                if (target) {
                    setDisabled(false)
                } else {
                    setDisabled(true)
                }
            } else {
                setDisabled(false)
            }
        }
    }, [materialsList])

    return (
        <div className='flex items-center'>
            <span style={{ lineHeight: '32px', padding: '4px 0 5px' }}>{currencySymbol}</span>
            <TextField
                type="text"
                variant='standard'
                className='w-full mb-5 px-0 pt-0'
                value={unitPrice}
                onChange={(e) => handleNumber({
                    event: e,
                    setter: setUnitPrice,
                    precision: 2
                })}
                onBlur={e => handleZero({
                    event: e,
                    setter: setUnitPrice
                })}
                onFocus={e => deleteZero({
                    event: e,
                    setter: setUnitPrice,
                    value: unitPrice
                })}
            />
        </div>
    )
}

const MaterialTotalPrice = ({ params, t, currencySymbol, calculateTotalCost, qtyToProduce }) => {
    const [totalPrice, setTotalPrice] = useState(0)

    useEffect(() => {
        setTotalPrice(round((params.row.qty_per_assembly * params.row.unit_price) * qtyToProduce, 2))
        calculateTotalCost()
    }, [params.row.qty_per_assembly, params.row.unit_price, qtyToProduce])

    return (
        <>{currencySymbol}{totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}

const MaterialActions = ({ params, materials, setMaterials, setMaterialsList }) => {

    const { t } = useTranslation()

    const deleteMaterial = async () => {
        const target = materials.filter(m => m.id !== params.row.id)
        setMaterials(target)
        setMaterialsList(prev => {
            const material = prev.find(m => m.id == params.row.item_id)
            if (Boolean(material)) {
                material.disabled = false
            }
            return prev
        })
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={deleteMaterial}><i className="fa-solid fa-trash"></i></span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}

const OperationSelect = ({ params, t, operationOptions, handleOperationChange }) => {
    return (
        <FormControl variant="standard" sx={{ width: '100%' }}>
            <Select
                displayEmpty
                value={params.row.operation ?? ''}
                onChange={(e) => handleOperationChange(e.target.value, params.row.id)}
            >
                <MenuItem value="" disabled><em>{t('select_operation')}</em></MenuItem>
                {operationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const OperationWorkCenter = ({ params, workCenters, handleWorkCenterChange }) => {
    const optionsWorkCenters = workCenters?.map(option => ({ label: option.work_center_name, id: option.id }))

    return (
        <Autocomplete
            options={optionsWorkCenters}
            value={params.row.workCenterId ? { label: optionsWorkCenters.find(o => o.id === params.row.workCenterId)?.label ?? params.row.workCenterName ?? '', id: params.row.workCenterId } : null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(a, b) => handleWorkCenterChange(b, params.row.id)}
            className='w-full'
            renderInput={(p) => <TextField {...p} variant='standard' />}
        />
    )
}

const OperationRate = ({ params, handleProductionRateChange }) => {
    const [value, setValue] = useState(params.value)
    const [debouncedValue] = useDebounce(value, 250)

    useEffect(() => {
        setValue(params.value)
    }, [params.value])

    useEffect(() => {
        handleProductionRateChange(debouncedValue, params.row.id)
    }, [debouncedValue])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={value}
            onChange={(e) => handleNumber({ event: e, setter: setValue, precision: 2 })}
            onBlur={e => handleZero({ event: e, setter: setValue })}
            onFocus={e => deleteZero({ event: e, setter: setValue, value: value })}
        />
    )
}

const OperationRateUom = ({ params, t, rateUomOptions, handleRateUomChange }) => {
    return (
        <FormControl variant="standard" sx={{ width: '100%' }}>
            <Select
                displayEmpty
                value={params.row.rateUom ?? ''}
                onChange={(e) => handleRateUomChange(e.target.value, params.row.id)}
            >
                <MenuItem value="" disabled><em>{t('select_rate_uom')}</em></MenuItem>
                {rateUomOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const OperationSetupTime = ({ params, handleSetupTimeChange }) => {
    const [value, setValue] = useState(params.value)
    const [debouncedValue] = useDebounce(value, 250)

    useEffect(() => {
        setValue(params.value)
    }, [params.value])

    useEffect(() => {
        handleSetupTimeChange(debouncedValue, params.row.id)
    }, [debouncedValue])

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-full mb-5 px-0 pt-0'
            value={value}
            onChange={(e) => handleNumber({ event: e, setter: setValue, precision: 2 })}
            onBlur={e => handleZero({ event: e, setter: setValue })}
            onFocus={e => deleteZero({ event: e, setter: setValue, value: value })}
        />
    )
}
